import CONFIG from "config/config";
import { getReviewOfferEligibility } from "campaign-builder/checkout/billingApi";
import { ReviewOfferEligibilityResponse } from "./types";

// ---- Mock (until HUB-449's endpoint is confirmed wired end-to-end) ----
// Dev/QA override so a person testing locally can drive every state without a
// real backend: `localStorage.setItem("review_offer_mock_state", "S4")`.
// Valid values: "S1" | "S4" | "S8" | "error" (unset -> "S1" eligible default).
const MOCK_STATE_STORAGE_KEY = "review_offer_mock_state";

// HUB-466: mock responses stay on CHECKOUT_5/5% — they exist to drive the S1-
// S8 row states locally, not to exercise the two-program split (which is
// covered by unit tests mocking fetchReviewOfferEligibility directly).
const MOCK_RESPONSES: Record<string, ReviewOfferEligibilityResponse> = {
  S1: { eligible: true, reason: "ok", program: "CHECKOUT_5", discount_percent: 5, entitlement: null },
  S4: {
    eligible: true,
    reason: "entitlement_active",
    program: "CHECKOUT_5",
    discount_percent: 5,
    entitlement: {
      status: "active",
      program: "CHECKOUT_5",
      review_id: "rev_mock_0001",
      expires_at: new Date(Date.now() + 6 * 24 * 60 * 60 * 1000).toISOString(),
      discount_percent: 5,
    },
  },
  S8: { eligible: false, reason: "already_paid", program: "CHECKOUT_5", discount_percent: 5, entitlement: null },
};

const fetchMockEligibility = async (): Promise<ReviewOfferEligibilityResponse> => {
  const override = typeof window !== "undefined" ? window.localStorage.getItem(MOCK_STATE_STORAGE_KEY) : null;
  if (override === "error") {
    throw new Error("Mocked service_error");
  }
  return MOCK_RESPONSES[override ?? "S1"] ?? MOCK_RESPONSES.S1;
};

// ---- Real endpoint (HUB-449, shipped under the billing router) ----
// GET {CONFIG.API_URL}/billing/checkout/review-offer/eligibility — platform
// session auth, client resolved server-side from req.user.locked_client, no
// params. See campaign-builder/checkout/billingApi.ts.
const fetchRealEligibility = (): Promise<ReviewOfferEligibilityResponse> => getReviewOfferEligibility();

/**
 * Single swap point: flip CONFIG.REVIEW_OFFER_ELIGIBILITY_MOCK to `false`
 * (or set REACT_APP_REVIEW_OFFER_ELIGIBILITY_MOCK=false) to use the real
 * GET /billing/checkout/review-offer/eligibility. No other call site changes.
 */
export const fetchReviewOfferEligibility = (): Promise<ReviewOfferEligibilityResponse> =>
  CONFIG.REVIEW_OFFER_ELIGIBILITY_MOCK ? fetchMockEligibility() : fetchRealEligibility();
